import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {addGenericErrorToast} from '@albert-io/atomic';
import getFilters from '../getFilters';
import {SortOrder, StudentAssignmentSortField} from 'gql/types';
import type {StudentAssignment, StudentAssignmentsQueryVariables} from '../StudentAssignment.types';
import {useStudentAssignmentsQuery} from '../queries/StudentAssignments.queries.generated';
import type {Value as DropdownValue} from '../FilterDropdown';

const PAGINATION_LIMIT = 25;
const TO_DO_LIMIT = 15;

/**
 * comparison for names with numeric prefixes
 * Splits leading number segments (e.g. "3.10") from the rest and compares
 * each numeric part individually, so "3.2" < "3.10" < "3.11".
 * Falls back to localeCompare for non-numeric portions.
 */
const naturalNameCompare = (a: string, b: string): number => {
  // Match leading dotted-number prefix like "3.10" or "12.4.1"
  const numPrefixRegex = /^(\d+(?:\.\d+)*)/;
  const aMatch = a.match(numPrefixRegex);
  const bMatch = b.match(numPrefixRegex);

  if (aMatch && bMatch) {
    const aParts = aMatch[1].split('.').map(Number);
    const bParts = bMatch[1].split('.').map(Number);
    const len = Math.max(aParts.length, bParts.length);
    for (let i = 0; i < len; i++) {
      const aNum = aParts[i] ?? 0;
      const bNum = bParts[i] ?? 0;
      if (aNum !== bNum) return aNum - bNum;
    }
    // Numeric prefixes are equal — compare the rest of the string
    const aRest = a.slice(aMatch[1].length);
    const bRest = b.slice(bMatch[1].length);
    return aRest.localeCompare(bRest);
  }

  // Names without numeric prefixes sort after those with, or alphabetically among themselves
  if (aMatch) return -1;
  if (bMatch) return 1;
  return a.localeCompare(b);
};

export default function useAssignments(
  classroomId: string | undefined,
  dropdownValue: DropdownValue
) {
  const [hasNextPageAfterLoadingMore, setHasNextPageAfterLoadingMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [moreAssignments, setMoreAssignments] = useState<StudentAssignment[]>([]);

  const isLoadingMoreRef = useRef(false);

  function resetMoreAssignments() {
    setMoreAssignments([]);
    setHasNextPageAfterLoadingMore(true);
    isLoadingMoreRef.current = false;
    setIsLoadingMore(false);
  }

  const limit = dropdownValue === 'toDo' ? TO_DO_LIMIT : PAGINATION_LIMIT;

  const variables = useMemo<StudentAssignmentsQueryVariables>(
    () => ({
      filters: getFilters(classroomId, dropdownValue),
      limit,
      sort: [
        {
          field: StudentAssignmentSortField.DueDateProximity,
          order: SortOrder.Asc
        }
      ]
    }),
    [classroomId, dropdownValue, limit]
  );

  const {data, error, fetchMore, loading} = useStudentAssignmentsQuery({variables});
  const initialAssignments = data?.currentUser?.studentAssignments.results ?? [];
  const initialHasNextPage = data?.currentUser?.studentAssignments.hasNextPage ?? false;

  const allAssignments = [...initialAssignments, ...moreAssignments].sort((a, b) => {
    // only re-sort within same due date
    if (a.dueDate === b.dueDate) {
      return naturalNameCompare(a.name, b.name);
    }
    return 0;
  });
  const hasNextPage = initialHasNextPage && hasNextPageAfterLoadingMore;

  const offsetRef = useRef(allAssignments.length);
  offsetRef.current = allAssignments.length;

  const exposedFetchMore = useCallback(() => {
    if (!loading && !isLoadingMoreRef.current && hasNextPage) {
      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);

      fetchMore({
        variables: {
          ...variables,
          offset: offsetRef.current
        }
      })
        .then((fetchedData) => {
          const fetchedAssignments = fetchedData.data.currentUser?.studentAssignments.results ?? [];
          const fetchedHasNextPage =
            fetchedData.data.currentUser?.studentAssignments.hasNextPage ?? false;

          setMoreAssignments((current) => [...current, ...fetchedAssignments]);
          setHasNextPageAfterLoadingMore(fetchedHasNextPage);
        })
        .catch(() => {
          addGenericErrorToast();
        })
        .finally(() => {
          isLoadingMoreRef.current = false;
          setIsLoadingMore(false);
        });
    }
  }, [fetchMore, hasNextPage, loading, variables]);

  useEffect(() => {
    if (error !== undefined) {
      addGenericErrorToast();
    }
  }, [error]);

  return {
    assignments: allAssignments,
    fetchMore: exposedFetchMore,
    isLoadingMore,
    loading,
    resetMoreAssignments
  };
}
